export function toArray(value) {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
}

export function valueId(value) {
    if (!value && value !== 0) return "";
    if (typeof value === "object") return `${value.documentId ?? value.id ?? value.value ?? ""}`;
    return `${value}`;
}

export function relationId(value) {
    if (!value && value !== 0) return undefined;
    if (typeof value === "object") return value.id ?? value.documentId ?? value.value;
    return value;
}

export function uniqById(items) {
    const map = new Map();
    toArray(items).flat().filter(Boolean).forEach((item) => {
        const id = valueId(item);
        if (id && !map.has(id)) map.set(id, item);
    });
    return [...map.values()];
}

export function normalizeMembers(workspace) {
    return uniqById(toArray(workspace?.workspace_memberships));
}

export function derivePortfolios(projects) {
    const portfolios = [];
    toArray(projects).forEach((project) => {
        toArray(project?.portfolios).forEach((portfolio) => {
            portfolios.push({
                ...portfolio,
                projects: [...toArray(portfolio?.projects), project],
            });
        });
    });
    return uniqById(portfolios).map((portfolio) => ({
        ...portfolio,
        projects: uniqById(portfolio.projects),
    }));
}

export function reportIcon(chartStyle) {
    if (chartStyle === "line") return "pi pi-chart-line";
    if (chartStyle === "donut") return "pi pi-chart-pie";
    if (chartStyle === "number") return "pi pi-hashtag";
    return "pi pi-chart-bar";
}

export function buildReportDescription(report, translate) {
    const entity = translate(`reports.builder.entity.${report.config?.entity}`);
    const chart = translate(`reports.builder.chart.${report.config?.chartStyle}`);
    return `${entity} - ${chart}`;
}

function backendSource(source) {
    if (source === "workspace") return "workspace";
    if (source === "specificProject") return "project";
    if (source === "teamGoals") return "team";
    if (["myWork", "projectOwner", "goalOwner", "portfolioOwner"].includes(source)) return "member";
    return "custom";
}

export function getReportConfig(report) {
    return report?.config || {
        title: report?.title,
        entity: report?.entity,
        source: report?.source || report?.dataSource,
        sourceItems: report?.sourceItems || report?.sourceItemIds || [],
        sourceItemIds: report?.sourceItemIds || report?.sourceItems || [],
        chartStyle: report?.chartStyle,
        metric: report?.metric || report?.metrics?.[0],
        metrics: report?.metrics || (report?.metric ? [report.metric] : []),
        dimension: report?.dimension || report?.xAxis,
        xAxis: report?.xAxis || report?.dimension,
        groupBy: report?.groupBy,
        aggregation: report?.aggregation,
        dateField: report?.dateField,
        timeUnit: report?.timeUnit,
        filters: report?.filters || [],
        annotations: report?.annotations || {},
    };
}

export function normalizeSavedReport(report, translate) {
    const config = getReportConfig(report);
    const title = report?.title || config?.title || translate("reports.builder.defaultTitle");
    return {
        ...report,
        id: valueId(report) || valueId(report?.documentId) || `${title}-${report?.createdAt || ""}`,
        title,
        description: report?.description || buildReportDescription({config}, translate),
        icon: report?.icon || reportIcon(config?.chartStyle),
        author: report?.creatorUser?.member?.fullname || translate("reports.page.byYou"),
        time: report?.updatedAt ? new Date(report.updatedAt).toLocaleDateString("fa-IR") : translate("reports.page.saved"),
        config,
        system: false,
    };
}

export function buildReportPayload({config, preview, rowsCount, workspaceId, reportDashboardId, translate, forUpdate = false}) {
    const title = config.title?.trim() || preview?.title || translate("reports.builder.defaultTitle");
    const normalizedConfig = {
        ...config,
        title,
    };
    const metrics = normalizedConfig.metrics?.length ? normalizedConfig.metrics : (normalizedConfig.metric ? [normalizedConfig.metric] : []);
    const dimension = normalizedConfig.dimension || normalizedConfig.xAxis;
    const sourceItems = normalizedConfig.sourceItems || normalizedConfig.sourceItemIds || [];
    const annotations = normalizedConfig.annotations || {
        showDataLabels: normalizedConfig.showDataLabels,
        showBenchmark: normalizedConfig.showBenchmark,
        benchmarkValue: normalizedConfig.benchmarkValue,
    };
    return {
        title,
        description: buildReportDescription({config: normalizedConfig}, translate),
        // a saved report keeps its workspace and dashboard when it is edited
        ...(forUpdate ? {} : {workspace: workspaceId, report_dashboard: reportDashboardId, reportStatus: "active"}),
        schemaVersion: "1",
        entity: normalizedConfig.entity,
        source: backendSource(normalizedConfig.source),
        sourceItemIds: sourceItems,
        chartStyle: normalizedConfig.chartStyle,
        metrics,
        dimension,
        xAxis: dimension,
        groupBy: normalizedConfig.groupBy,
        aggregation: normalizedConfig.aggregation,
        dateField: normalizedConfig.dateField,
        timeUnit: normalizedConfig.timeUnit,
        filters: normalizedConfig.filters || [],
        annotations,
        config: normalizedConfig,
        icon: reportIcon(normalizedConfig.chartStyle),
        lastRowsCount: rowsCount,
    };
}
